import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import type { Day } from 'lumen/models';
import type { MarketDemandLevel } from 'lumen/utils/constants/general-constants';
import { MARKET_DEMAND_LEVELS_CLASSES } from 'lumen/utils/constants/general-constants';
import { getDemandLevel } from 'lumen/utils/demand-level';

interface DemandChange {
  demand: string;
  extract_date: string;

  id: string;
  leadtime: number;
  message: string;
  value: number;
}

const MESSAGES = {
  MISSING: 'general.missing',
  OUTSIDE_RANGE: 'demand.outside_allowed_date_range',
};

export default class Demand extends Model {
  [Type] = 'demand' as const;

  @belongsTo('day', { async: false, inverse: 'demand' })
  declare day: Day;

  @attr() declare demandChanges: DemandChange[];
  @attr('dayjs') declare extractDatetime: DayjsTransformReturn;
  @attr('string') declare message: (typeof MESSAGES)[keyof typeof MESSAGES];
  @attr('number') declare value: number;

  @service declare intl: Services['intl'];

  get demandLevel(): MarketDemandLevel | null {
    if (this.isMissing) {
      return null;
    }

    return getDemandLevel(this.value);
  }

  get demandLevelClass(): string {
    if (this.isMissing) {
      return '';
    }

    return MARKET_DEMAND_LEVELS_CLASSES[this.demandLevel as MarketDemandLevel];
  }

  get formattedDemandLevel(): string {
    return this.intl.t(`general.demand.${this.demandLevel}`);
  }

  get formattedValue(): string {
    if (this.isOutOfRange) {
      return ''; // Show empty string if demand is out of range (data is expected to be missing)
    }

    return this.message
      ? this.intl.t(this.message)
      : this.intl.formatNumber(this.value, { style: 'percent' });
  }

  get isMissing(): boolean {
    return this.message === MESSAGES.MISSING;
  }

  get isOutOfRange(): boolean {
    return this.message === MESSAGES.OUTSIDE_RANGE;
  }
}
